/* eslint-disable max-len */
import {
  Button,
  FunctionField,
  useSidebarState,
  useTranslate,
  useListContext,
  Pagination
} from "react-admin";
import * as User from "providers/user";
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Theme,
  useMediaQuery,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import CustomReportingCalendar from "./CustomReportingCalendar";
import CustomDataGrid from "./CustomDataGrid";
import CustomList from "./CustomList";
import { DownloadIcon, EmptyReportIcon } from "assets/Icons";
import { useAxios } from "providers/axios";
import { useLocation } from "react-router-dom";
import {
  getZonedDate,
  getStartAndEndTime,
  formatCurrency
} from "utils/helpers";
import CustomEmptyList from "./CustomEmptyList";

const PostPagination = (props: any) => (
  <Pagination rowsPerPageOptions={[10, 25, 50, 100]} {...props} />
);

function TransactionsSummary() {
  const translate = useTranslate();
  const { data } = useListContext();
  if (!data) {
    return null;
  }
  const total = Object.values(data).reduce((acc: number, record: any) => {
    return acc + (record.transactionTotal || 0);
  }, 0);
  return (
    <div
      style={{
        padding: "8px",
        fontWeight: "bold",
        textAlign: "right",
        marginRight: "12px"
        // backgroundColor: "#f7f7f7"
      }}
    >
      {`${translate("report.transaction_total")}: $${total.toFixed(2)}`}
    </div>
  );
}

export default function TransactionsReportList() {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const isSmall = useMediaQuery<Theme>("(max-width:599px)");
  const isTitleHidden = useMediaQuery<Theme>("(max-width:705px)");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isSideBarOpen, _setIsSideBarOpen] = useSidebarState();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(
    () => {
      return (
        localStorage.getItem("transactionsReportPaymentMethodFilter") || "all"
      );
    }
  );
  const [selectedView, setSelectedView] = useState<string>("day");
  const scrollRef = useRef<any>(null);
  // const dateFilterFromLocalStorage = localStorage.getItem("ReportDateFilter");
  const dateFilterFromLocalStorage = null;
  const parsedDateFilter =
    dateFilterFromLocalStorage !== null && dateFilterFromLocalStorage !== "null"
      ? dayjs(JSON.parse(dateFilterFromLocalStorage))
      : dayjs();
  const [calendarsDate, setCalendarsDate] = useState<Dayjs | null>(
    parsedDateFilter
  );
  // get page, perPage from url using react-router-dom
  const location = useLocation();

  const [selectedCalendarDate, setSelectedCalendarDate] = useState<{
    title: string;
    dateForFilter: string;
  } | null>(null);
  const { api } = useAxios();

  const locationIds = permissions.isArtist
    ? user?.joined_company?.locations?.map((location: any) => location.id)
    : permissions.impersonateLocationIds.length > 0
    ? permissions.impersonateLocationIds
    : user?.company?.locations?.map((location: any) => location.id);

  useEffect(() => {
    localStorage.removeItem("ReportDateFilter");
    // We need setTimeout because on componentDidMount ref current is undefined, it needs short time to initialize
    const scrollTimer = setTimeout(() => {
      scrollRef?.current?.scrollBy({
        top: 800
      });
    }, 500);
    return () => clearTimeout(scrollTimer);
  }, []);

  const locationIdsString = JSON.stringify(locationIds);

  const locations = permissions.isArtist
    ? user?.joined_company?.locations
    : user?.company?.locations;
  const timezone = locations?.[0]?.timezone || "PST8PDT";

  const startTime = getStartAndEndTime(
    selectedCalendarDate?.dateForFilter,
    selectedView,
    timezone
  );

  const startTimeString = JSON.stringify(startTime);

  const handlePaymentMethodChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    localStorage.setItem("transactionsReportPaymentMethodFilter", value);
    setSelectedPaymentMethod(value);
  };

  const onExport = () => {
    const token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${token}`
    };
    const paymentMethodParam =
      selectedPaymentMethod && selectedPaymentMethod !== "all"
        ? `&paymentMethod=${selectedPaymentMethod}`
        : "";
    const url = `/v1/reporting/transactions?locationIds=${locationIdsString}&startTime=${startTimeString}${paymentMethodParam}`;

    api.get(url, { headers }).then((response: any) => {
      const data = response.data.data;
      const columns = response.data.columns;
      let csv = columns.join(",") + "\n";
      data.forEach((row: any) => {
        csv += columns.map((column: string) => row[column]).join(",");
        csv += "\n";
      });
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders_${selectedCalendarDate?.title}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        position: "relative"
      }}
    >
      <div
        className={`absolute ${
          user.impersonate_company_id ? "top-[51px]" : "top-[21px]"
        } ${
          isSmall ? "right-[64px]" : "right-[124px]"
        } flex gap-[8px] z-50 cursor-default`}
      >
        <FormControl
          sx={{
            minWidth: 86,
            maxWidth: isSmall ? 135 : 145,
            mt: "-6px",
            mb: "0px"
          }}
          size="small"
        >
          <InputLabel id="payment-method-select-label">
            {translate("amenities.type")}
          </InputLabel>
          <Select
            labelId="payment-method-select-label"
            id="payment-method-select"
            value={selectedPaymentMethod}
            label={translate("common.locations")}
            onChange={handlePaymentMethodChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="STRIPE">CARD ON FILE</MenuItem>
            <MenuItem value="POS">POS</MenuItem>
            <MenuItem value="CASH">Cash</MenuItem>
            <MenuItem value="GIFTCARD">Gift Card</MenuItem>
            <MenuItem value="REFUND">MERCH CREDIT</MenuItem>
          </Select>
        </FormControl>
        <CustomReportingCalendar
          calendarsDate={calendarsDate}
          setCalendarsDate={setCalendarsDate}
          calendarsDateTitle={selectedCalendarDate?.title || ""}
          setSelectedCalendarDate={setSelectedCalendarDate}
          setSelectedView={setSelectedView}
          selectedView={selectedView}
          className="flex gap-[16px] z-50 cursor-default"
          timezone={timezone}
        />
      </div>
      <CustomList
        empty={false}
        title={isTitleHidden ? "" : translate("report.transactions")}
        hasCreate={false}
        exporter={false}
        queryOptions={{
          meta: {
            startTimeString,
            locationIdsString,
            paymentMethod:
              selectedPaymentMethod !== "all"
                ? selectedPaymentMethod
                : undefined
          }
        }}
        customExport={
          <Button label="ra.action.export" onClick={onExport}>
            <DownloadIcon />
          </Button>
        }
        key={location.search}
        reportingListsWidth="calc(100vw - 34px)"
        pagination={<PostPagination />}
      >
        <CustomDataGrid
          rowClick={false}
          hover={false}
          empty={
            <div
              style={{
                height: "calc(100vh - 200px)",
                backgroundColor: "#F6F4F1"
              }}
            >
              <CustomEmptyList
                emptyIcon={<EmptyReportIcon />}
                emptyTitle={translate("empty_list.no_results_found")}
                emptyListCreateSubtitle=""
                createButtonText=""
                hasCreate={false}
              />
            </div>
          }
          sx={{
            borderRadius: "0px",
            ".RaDatagrid-headerCell": {
              backgroundColor: "transparent",
              minWidth: "0px",
              fontSize: "14px",
              lineHeight: "18px",
              "@media screen and (max-width:1280px)": {
                fontSize: "12px"
              },
              "@media screen and (max-width:968px)": {
                paddingLeft: "8px",
                paddingRight: "8px"
              }
            },
            ".RaDatagrid-rowCell": {
              minWidth: "fit-content",
              padding: "10px 16px",
              "@media screen and (max-width:968px)": {
                paddingLeft: "8px",
                paddingRight: "8px"
              }
            },
            ".RaDatagrid-rowCell span": {
              fontSize: "14px",
              "@media screen and (max-width:1280px)": {
                fontSize: "12px"
              }
            }
          }}
        >
          <FunctionField
            label={translate("report.transaction_type")}
            render={(record: any) => (
              <div className="capitalize whitespace-nowrap line-clamp-1 break-all">
                {record?.transactionRefundCapture}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.status")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.transactionStatus}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.transaction_date_time")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {dayjs(
                  getZonedDate(record?.transactionDateTime, record.timezone)
                ).format("MM/DD/YYYY hh:mm A")}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.transaction_services_total")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {formatCurrency(record?.transactionServicesTotal)}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.transaction_tip_amount")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.transactionTipAmount
                  ? formatCurrency(record.transactionTipAmount)
                  : "-"}
              </div>
            )}
          />
          {/* <FunctionField
            label={translate("report.transaction_user_pays_fees")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.transacitonUserPaysFees ? "Yes" : "No"}
              </div>
            )}
          /> */}
          {/* <FunctionField
            label={translate("report.transaction_payment_fees_percent")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.transactionPaymentFeesPercent}
              </div>
            )}
          /> */}
          {/* <FunctionField
            label={translate("report.transaction_payment_fees")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                ${record?.transactionPaymentFees}
              </div>
            )}
          /> */}
          <FunctionField
            label={translate("report.transaction_total")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {formatCurrency(record?.transactionTotal)}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.cc_payout")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {formatCurrency(record?.transactionPayout)}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.location_name")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.locationName}</div>
            )}
          />

          <FunctionField
            label={translate("report.transaction_description")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.transactionDescription}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.customer_name")}
            render={(record: any) => (
              <div className="line-clamp-1 break-all min-w-[150px]">
                {record?.customerName}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.appointment_date_time")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {dayjs(record?.appointmentDateTime).format(
                  "MM/DD/YYYY hh:mm A"
                )}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.appointment_description")}
            render={(record: any) => (
              <div className="whitespace-nowrap">
                {record?.appointmentDescription}
              </div>
            )}
          />
          <FunctionField
            label={translate("report.order_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.orderId}</div>
            )}
          />
          <FunctionField
            label={translate("report.transaction_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.transactionId}</div>
            )}
          />
          <FunctionField
            label={translate("report.location_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.locationId}</div>
            )}
          />
          <FunctionField
            label={translate("report.stripe_id")}
            render={(record: any) => (
              <div className="whitespace-nowrap">{record?.stripeId}</div>
            )}
          />
        </CustomDataGrid>
        <TransactionsSummary />
      </CustomList>
    </Box>
  );
}
